<template>
  <div
    aria-label="Gestion du personnel logistique"
    role="main"
  >
    <h1
      id="page-title"
      class="sr-only"
    >
      Gestion des membres du personnel logistique
    </h1>
    <p
      id="page-desc"
      class="sr-only"
    >
      Cette page permet de visualiser, filtrer et gérer les membres du personnel logistique. Utilisez les filtres pour affiner la liste. Les actions sont accessibles via les boutons dédiés.
    </p>
    <!-- Statistiques synthétiques -->
    <b-row
      class="mb-3"
      aria-label="Statistiques synthétiques du personnel"
    >
      <b-col
        lg="4"
        md="6"
        sm="12"
        class="mb-2"
      >
        <b-card class="shadow-sm border-0">
          <div class="d-flex align-items-center justify-content-between">
            <div>
              <h6
                class="text-muted"
                aria-label="Nombre total de membres du personnel"
              >
                Total membres du personnel
              </h6>
              <h3 class="font-weight-bolder mb-0">
                {{ summaryStats.total || 0 }}
              </h3>
            </div>
            <div class="avatar bg-light-primary p-2">
              <feather-icon
                icon="UsersIcon"
                class="text-primary"
                size="18"
              />
            </div>
          </div>
        </b-card>
      </b-col>

      <b-col
        lg="4"
        md="6"
        sm="12"
        class="mb-2"
      >
        <b-card class="shadow-sm border-0">
          <div class="d-flex align-items-center justify-content-between">
            <div>
              <h6
                class="text-muted"
                aria-label="Nombre de membres actifs"
              >
                Membres actifs
              </h6>
              <h3 class="font-weight-bolder mb-0 text-success">
                {{ summaryStats.active || 0 }}
              </h3>
            </div>
            <div class="avatar bg-light-success p-2">
              <feather-icon
                icon="CheckCircleIcon"
                class="text-success"
                size="18"
              />
            </div>
          </div>
        </b-card>
      </b-col>

      <b-col
        lg="4"
        md="6"
        sm="12"
        class="mb-2"
      >
        <b-card class="shadow-sm border-0">
          <div class="d-flex align-items-center justify-content-between">
            <div>
              <h6
                class="text-muted"
                aria-label="Nombre de membres inactifs"
              >
                Membres inactifs
              </h6>
              <h3 class="font-weight-bolder mb-0 text-secondary">
                {{ summaryStats.inactive || 0 }}
              </h3>
            </div>
            <div class="avatar bg-light-secondary p-2">
              <feather-icon
                icon="XCircleIcon"
                class="text-secondary"
                size="18"
              />
            </div>
          </div>
        </b-card>
      </b-col>
    </b-row>

    <!-- Statistiques par rôle logistique -->
    <b-row
      class="mb-3"
      aria-label="Statistiques par rôle logistique"
    >
      <b-col
        v-for="role in staffRoles"
        :key="role.value"
        lg="3"
        md="6"
        sm="12"
        class="mb-2"
      >
        <b-card class="shadow-sm border-0">
          <div class="d-flex align-items-center justify-content-between">
            <div>
              <h6
                class="text-muted"
                :aria-label="'Nombre de ' + role.label.toLowerCase()"
              >
                {{ role.label }}
              </h6>
              <h3 class="font-weight-bolder mb-0 text-info">
                {{ summaryStats[role.value] || 0 }}
              </h3>
            </div>
            <div class="avatar bg-light-info p-2">
              <feather-icon
                icon="UserCheckIcon"
                class="text-info"
                size="18"
              />
            </div>
          </div>
        </b-card>
      </b-col>
    </b-row>

    <!-- Barre de recherche et filtres -->
    <b-card
      class="mb-3"
      aria-label="Filtres et actions du personnel logistique"
    >
      <b-row>
        <!-- Search Field -->
        <b-col
          md="4"
          sm="6"
        >
          <b-form-group>
            <label for="search-input">Recherche</label>
            <b-input-group>
              <b-form-input
                id="search-input"
                v-model="searchTerm"
                placeholder="Rechercher par nom, email..."
                aria-label="Recherche par nom ou email"
                @input="onSearchChange"
              />
              <b-input-group-append>
                <b-button
                  variant="primary"
                  @click="getStaff"
                >
                  <feather-icon
                    icon="SearchIcon"
                    size="16"
                  />
                </b-button>
              </b-input-group-append>
            </b-input-group>
          </b-form-group>
        </b-col>

        <!-- Repository Filter -->
        <b-col
          md="4"
          sm="6"
        >
          <b-form-group>
            <label for="repo-filter">Dépôt</label>
            <v-select
              id="repo-filter"
              v-model="filters.repository"
              :options="repositories"
              label="name"
              placeholder="Tous les dépôts"
              :reduce="repo => repo.id"
              clearable
              class="enhanced-select border-select"
              aria-label="Filtrer par dépôt logistique"
              @input="getStaff"
            />
            <small
              id="repo-filter-desc"
              class="sr-only"
            >Le dépôt est requis uniquement pour les opérateurs de dépôt.</small>
          </b-form-group>
        </b-col>

        <!-- Status Filter -->
        <b-col
          md="4"
          sm="6"
        >
          <b-form-group>
            <label for="status-filter">Statut</label>
            <v-select
              id="status-filter"
              v-model="filters.is_active"
              :options="[
                {label: 'Tous', value: null},
                {label: 'Actif', value: true},
                {label: 'Inactif', value: false}
              ]"
              label="label"
              :reduce="item => item.value"
              placeholder="Tous"
              clearable
              class="enhanced-select border-select"
              aria-label="Filtrer par statut du personnel"
              @input="getStaff"
            />
          </b-form-group>
        </b-col>
      </b-row>

      <!-- Action Buttons -->
      <div
        class="d-flex justify-content-between mt-3"
        aria-label="Actions principales"
      >
        <div>
          <b-button
            v-b-tooltip.hover
            title="Réinitialiser les filtres"
            variant="outline-secondary"
            class="mr-1"
            @click="resetFilters"
          >
            <feather-icon
              icon="RefreshCwIcon"
              size="16"
            />
          </b-button>
        </div>

        <div>
          <b-button
            v-b-tooltip.hover
            title="Vue Carte/Liste"
            :variant="viewMode === 'table' ? 'outline-secondary' : 'outline-primary'"
            class="mr-1"
            @click="toggleViewMode"
          >
            <feather-icon
              :icon="viewMode === 'table' ? 'GridIcon' : 'ListIcon'"
              size="16"
            />
          </b-button>

          <b-button
            v-b-tooltip.hover
            title="Exporter en CSV"
            variant="outline-success"
            class="mr-1"
            @click="exportToCSV"
          >
            <feather-icon
              icon="DownloadIcon"
              size="16"
            />
          </b-button>

          <b-button
            :to="{ name: 'staff-add' }"
            variant="primary"
          >
            <feather-icon icon="PlusIcon" />
            <span class="ml-1">Ajouter un membre</span>
          </b-button>
        </div>
      </div>
    </b-card>

    <!-- État de chargement -->
    <div
      v-if="isLoading"
      class="text-center mb-2"
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <b-spinner
        variant="primary"
        aria-hidden="true"
      />
      <span class="sr-only">Chargement des membres du personnel...</span>
    </div>

    <!-- Vue carte -->
    <div
      v-else-if="viewMode === 'card' && !isLoading"
      aria-label="Vue carte des membres du personnel"
    >
      <b-row>
        <b-col
          v-for="staff in staffList"
          :key="staff.id"
          lg="4"
          md="6"
          sm="12"
          class="mb-3"
        >
          <b-card
            no-body
            class="supplier-card h-100"
            :class="{ 'border-success': staff.is_active, 'border-secondary': !staff.is_active }"
          >
            <b-card-body>
              <div class="d-flex justify-content-between align-items-center mb-2">
                <h5
                  class="mb-0"
                  :class="staff.is_active ? 'text-dark' : 'text-muted'"
                >
                  {{ staff.username || 'Sans nom' }}
                </h5>
                <b-badge
                  pill
                  :variant="staff.is_active ? 'success' : 'secondary'"
                  class="px-3 py-2"
                >
                  <feather-icon
                    :icon="staff.is_active ? 'CheckCircleIcon' : 'XCircleIcon'"
                    size="12"
                    class="mr-1"
                  />
                  {{ staff.is_active ? 'Actif' : 'Inactif' }}
                </b-badge>
              </div>

              <div class="supplier-info mb-3">
                <p class="mb-1">
                  <feather-icon
                    icon="MailIcon"
                    size="14"
                    class="mr-1"
                  />
                  {{ staff.email || 'N/A' }}
                </p>
                <p class="mb-1">
                  <feather-icon
                    icon="PhoneIcon"
                    size="14"
                    class="mr-1"
                  />
                  {{ staff.phone || 'N/A' }}
                </p>
                <p class="mb-1">
                  <feather-icon
                    icon="UserIcon"
                    size="14"
                    class="mr-1"
                  />
                  {{ getRoleLabel(staff.role) }}
                </p>
                <p class="mb-1">
                  <feather-icon
                    icon="HomeIcon"
                    size="14"
                    class="mr-1"
                  />
                  {{ typeof staff.repository === 'string' ? staff.repository :
                    (typeof staff.repository === 'object' && staff.repository ? staff.repository.name : 'N/A') }}
                </p>
              </div>

              <div class="d-flex justify-content-between align-items-center">
                <div />
                <div>
                  <b-button-group size="sm">
                    <b-button
                      v-b-tooltip.hover
                      title="Voir détails"
                      variant="outline-primary"
                      :to="{ name: 'staff-show', params: { id: staff.id }}"
                    >
                      <feather-icon
                        icon="EyeIcon"
                        size="14"
                      />
                    </b-button>

                    <b-button
                      v-b-tooltip.hover
                      title="Modifier"
                      variant="outline-secondary"
                      :to="{ name: 'staff-edit', params: { id: staff.id }}"
                    >
                      <feather-icon
                        icon="EditIcon"
                        size="14"
                      />
                    </b-button>

                    <b-button
                      v-b-tooltip.hover
                      :title="staff.is_active ? 'Désactiver' : 'Activer'"
                      :variant="staff.is_active ? 'outline-warning' : 'outline-success'"
                      @click="toggleStaffStatus(staff)"
                    >
                      <feather-icon
                        :icon="staff.is_active ? 'PowerIcon' : 'CheckCircleIcon'"
                        size="14"
                      />
                    </b-button>
                  </b-button-group>
                </div>
              </div>
            </b-card-body>
          </b-card>
        </b-col>
      </b-row>
    </div>

    <!-- Vue tableau accessible -->
    <vue-good-table
      v-else
      :columns="columns"
      :rows="staffList"
      :search-options="{
        enabled: true,
        externalQuery: searchTerm,
        placeholder: 'Rechercher dans le tableau...'
      }"
      :pagination-options="{
        enabled: true,
        perPage: pageLength,
      }"
      :sort-options="{
        enabled: true,
        initialSortBy: {field: 'username', type: 'asc'}
      }"
      style-class="vgt-table table-responsive-sm striped bordered condensed"
      aria-label="Tableau des membres du personnel logistique"
      role="table"
    >
      <template
        slot="table-row"
        slot-scope="props"
      >
        <!-- Username column with enhanced avatar -->
        <div
          v-if="props.column.field === 'username'"
          class="d-flex align-items-center"
        >
          <div
            class="avatar-wrapper rounded-circle mr-2 d-flex justify-content-center align-items-center position-relative"
            :class="props.row.is_active ? 'bg-light-primary' : 'bg-light-secondary'"
            style="width: 42px; height: 42px; min-width: 42px;"
          >
            <span
              class="font-weight-bold text-uppercase"
              :class="props.row.is_active ? 'text-primary' : 'text-secondary'"
            >
              {{ getInitials(props.row.username) }}
            </span>
            <div
              v-if="!props.row.is_active"
              class="status-indicator position-absolute bg-secondary"
              style="width: 12px; height: 12px; border-radius: 50%; top: -2px; right: -2px; border: 2px solid white;"
            />
            <div
              v-else
              class="status-indicator position-absolute bg-success"
              style="width: 12px; height: 12px; border-radius: 50%; top: -2px; right: -2px; border: 2px solid white;"
            />
          </div>
          <div>
            <div
              class="font-weight-semibold"
              :class="props.row.is_active ? 'text-dark' : 'text-muted'"
            >
              {{ props.row.username || 'Sans nom' }}
            </div>
            <small class="text-muted d-block">
              {{ getRoleLabel(props.row.role) }}
            </small>
          </div>
        </div>

        <!-- Contact info column -->
        <div v-else-if="props.column.field === 'contact_info'">
          <div class="contact-info">
            <div class="d-flex align-items-center mb-1">
              <feather-icon
                icon="MailIcon"
                size="14"
                class="text-primary mr-1"
              />
              <small
                class="text-truncate"
                style="max-width: 180px;"
                :title="props.row.email"
              >
                {{ props.row.email || 'N/A' }}
              </small>
            </div>
            <div class="d-flex align-items-center">
              <feather-icon
                icon="PhoneIcon"
                size="14"
                class="text-success mr-1"
              />
              <small>{{ props.row.phone || 'N/A' }}</small>
            </div>
          </div>
        </div>

        <!-- Role column -->
        <div v-else-if="props.column.field === 'role'">
          <div class="d-flex align-items-center">
            <feather-icon
              icon="UserIcon"
              size="16"
              class="text-info mr-2"
            />
            <span class="font-weight-semibold">
              {{ getRoleLabel(props.row.role) }}
            </span>
          </div>
        </div>

        <!-- Repository column enhanced -->
        <div v-else-if="props.column.field === 'repository'">
          <div class="d-flex align-items-center">
            <feather-icon
              icon="HomeIcon"
              size="16"
              class="text-primary mr-2"
            />
            <div>
              <span class="font-weight-semibold">
                {{ typeof props.row.repository === 'string' ? props.row.repository :
                  (typeof props.row.repository === 'object' && props.row.repository ? props.row.repository.name : 'N/A') }}
              </span>
            </div>
          </div>
        </div>

        <!-- Enhanced Status column -->
        <div v-else-if="props.column.field === 'is_active'">
          <div class="text-center">
            <b-badge
              :variant="props.row.is_active ? 'success' : 'secondary'"
              pill
              class="status-badge px-2 py-1 font-weight-semibold"
              style="font-size: 0.75rem; min-width: 60px;"
            >
              <feather-icon
                :icon="props.row.is_active ? 'CheckCircleIcon' : 'XCircleIcon'"
                size="12"
                class="mr-1"
              />
              {{ props.row.is_active ? 'Actif' : 'Inactif' }}
            </b-badge>
          </div>
        </div>

        <!-- Enhanced Action column -->
        <div v-else-if="props.column.field === 'action'">
          <div class="action-buttons d-flex justify-content-center">
            <b-button
              v-b-tooltip.hover.top="'Voir détails'"
              variant="flat-primary"
              class="btn-icon rounded-circle mr-1"
              size="sm"
              :to="{ name: 'staff-show', params: { id: props.row.id }}"
            >
              <feather-icon
                icon="EyeIcon"
                size="14"
              />
            </b-button>

            <b-button
              v-b-tooltip.hover.top="'Modifier'"
              variant="flat-secondary"
              class="btn-icon rounded-circle mr-1"
              size="sm"
              :to="{ name: 'staff-edit', params: { id: props.row.id }}"
            >
              <feather-icon
                icon="EditIcon"
                size="14"
              />
            </b-button>

            <b-button
              v-b-tooltip.hover.top="props.row.is_active ? 'Désactiver' : 'Activer'"
              :variant="props.row.is_active ? 'flat-warning' : 'flat-success'"
              class="btn-icon rounded-circle"
              size="sm"
              @click="toggleStaffStatus(props.row)"
            >
              <feather-icon
                :icon="props.row.is_active ? 'PowerIcon' : 'CheckCircleIcon'"
                size="14"
              />
            </b-button>
          </div>
        </div>

        <!-- Default column rendering -->
        <span v-else>{{ props.formattedRow[props.column.field] }}</span>
      </template>

      <!-- Pagination -->
      <template
        slot="pagination-bottom"
        slot-scope="props"
      >
        <div class="d-flex justify-content-between flex-wrap">
          <div class="d-flex align-items-center mb-0 mt-1">
            <span class="text-nowrap">
              Affichage 1 à
            </span>
            <b-form-select
              v-model="pageLength"
              :options="['50','100','500']"
              class="mx-1"
              @input="(value)=>props.perPageChanged({currentPerPage:value})"
            />
            <span class="text-nowrap "> de {{ props.total }} résultats trouvés </span>
          </div>
          <div>
            <b-pagination
              :value="1"
              :total-rows="props.total"
              :per-page="pageLength"
              first-number
              last-number
              align="right"
              prev-class="prev-item"
              next-class="next-item"
              class="mt-1 mb-0"
              @input="(value)=>props.pageChanged({currentPage:value})"
            >
              <template #prev-text>
                <feather-icon
                  icon="ChevronLeftIcon"
                  size="18"
                />
              </template>
              <template #next-text>
                <feather-icon
                  icon="ChevronRightIcon"
                  size="18"
                />
              </template>
            </b-pagination>
          </div>
        </div>
      </template>
    </vue-good-table>

    <!-- Aucun membre trouvé -->
    <b-card
      v-if="!isLoading && staffList.length === 0"
      class="text-center py-5"
      aria-label="Aucun membre du personnel trouvé"
      role="status"
      aria-live="polite"
    >
      <div class="empty-state-icon mb-3">
        <feather-icon
          icon="UsersIcon"
          size="60"
          class="text-muted opacity-25"
          aria-hidden="true"
        />
      </div>
      <h4 id="empty-title">
        Aucun membre du personnel trouvé
      </h4>
      <p
        id="empty-desc"
        class="text-muted"
      >
        Ajoutez des membres du personnel ou modifiez vos critères de recherche pour voir les résultats.
      </p>
      <b-button
        :to="{ name: 'staff-add' }"
        variant="primary"
        aria-label="Ajouter un membre du personnel"
      >
        <feather-icon
          icon="PlusIcon"
          aria-hidden="true"
        />
        <span class="ml-1">Ajouter un membre</span>
      </b-button>
    </b-card>
  </div>
</template>

<script>
import axios from 'axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import vSelect from 'vue-select'
import { VueGoodTable } from 'vue-good-table'
import {
  BButton,
  BCard,
  BCardBody,
  BBadge,
  BRow,
  BCol,
  BFormInput,
  BFormGroup,
  BSpinner,
  BButtonGroup,
  BInputGroup,
  BInputGroupAppend,
  BPagination,
  BFormSelect,
} from 'bootstrap-vue'
import storeAuth from '@/store/store'

export default {
  components: {
    VueGoodTable,
    vSelect,
    BButton,
    BCard,
    BCardBody,
    BBadge,
    BRow,
    BCol,
    BFormInput,
    BFormGroup,
    BSpinner,
    BButtonGroup,
    BInputGroup,
    BInputGroupAppend,
    BPagination,
    BFormSelect,
  },

  data() {
    return {
      columns: [
        {
          label: 'Nom',
          field: 'username',
          sortable: true,
          filterOptions: {
            placeholder: 'Nom',
          },
          width: '200px',
        },
        {
          label: 'Contact',
          field: 'contact_info',
          sortable: false,
          width: '220px',
        },
        {
          label: 'Rôle',
          field: 'role',
          sortable: true,
          width: '120px',
        },
        {
          label: 'Dépôt',
          field: 'repository',
          sortable: true,
          filterOptions: {
            enabled: true,
            placeholder: 'Dépôt',
          },
          width: '150px',
          thAttrs: { title: 'Le dépôt est requis uniquement pour les opérateurs de dépôt' },
        },
        {
          label: 'Statut',
          field: 'is_active',
          sortable: true,
          width: '120px',
        },
        {
          label: 'Actions',
          field: 'action',
          sortable: false,
          width: '140px',
        },
      ],
      staffList: [],
      searchTerm: '',
      isLoading: false,
      pageLength: 100,
      viewMode: 'table',
      repositories: [],
      filters: {
        repository: null,
        is_active: null,
      },
      summaryStats: {
        total: 0,
        active: 0,
        inactive: 0,
        sales: 0,
        warehouse_operator: 0,
        customer_support: 0,
        accountant: 0,
      },
      staffRoles: [
        { label: 'Commerciaux', value: 'sales' },
        { label: 'Opérateurs Dépôt', value: 'warehouse_operator' },
        { label: 'Support Client', value: 'customer_support' },
        { label: 'Comptables', value: 'accountant' },
      ],
      searchTimer: null,
      user: {},
    }
  },

  created() {
    this.getStaff()
    this.user = storeAuth.state.user
    this.fetchRepositories()
  },

  methods: {
    async getStaff() {
      this.isLoading = true
      try {
        // Build params object for API call
        const params = {}
        if (this.filters.repository) params.repository = this.filters.repository
        if (this.filters.is_active !== null && this.filters.is_active !== undefined) params.is_active = this.filters.is_active
        if (this.searchTerm) params.search = this.searchTerm
        // Only staff roles
        params.role__in = 'sales,warehouse_operator,customer_support,accountant'
        const { data } = await axios.get('/api/auth/staff-members/', { params })
        this.staffList = data.results || []
        this.calculateSummaryStats()
      } catch (error) {
        this.showToast('danger', 'top-center', 'Erreur lors du chargement des membres du personnel')
      } finally {
        this.isLoading = false
      }
    },

    async fetchRepositories() {
      try {
        const { data } = await axios.get('/api/repositories/')
        this.repositories = data
      } catch (error) {
        this.showToast('danger', 'top-center', 'Erreur lors du chargement des dépôts')
      }
    },

    calculateSummaryStats() {
      if (!Array.isArray(this.staffList)) {
        this.summaryStats = {
          total: 0,
          active: 0,
          inactive: 0,
          sales: 0,
          warehouse_operator: 0,
          customer_support: 0,
          accountant: 0,
        }
        return
      }
      this.summaryStats = {
        total: this.staffList.length,
        active: this.staffList.filter(s => s.is_active).length,
        inactive: this.staffList.filter(s => !s.is_active).length,
        sales: this.staffList.filter(s => s.role === 'sales').length,
        warehouse_operator: this.staffList.filter(s => s.role === 'warehouse_operator').length,
        customer_support: this.staffList.filter(s => s.role === 'customer_support').length,
        accountant: this.staffList.filter(s => s.role === 'accountant').length,
      }
    },

    async toggleStaffStatus(staff) {
      const action = staff.is_active ? 'désactiver' : 'activer'
      const actionText = staff.is_active ? 'désactivé' : 'activé'
      try {
        const result = await this.$swal({
          title: `${action.charAt(0).toUpperCase() + action.slice(1)} le membre ?`,
          text: `Êtes-vous sûr de vouloir ${action} ${staff.username} ?`,
          icon: 'question',
          showCancelButton: true,
          confirmButtonText: `Oui, ${action}`,
          cancelButtonText: 'Annuler',
          customClass: {
            confirmButton: `btn ${staff.is_active ? 'btn-warning' : 'btn-success'}`,
            cancelButton: 'btn btn-outline-secondary ml-1',
          },
          buttonsStyling: false,
        })
        if (result.value) {
          this.isLoading = true
          try {
            await axios.put(`/api/auth/users/delete/${staff.id}/`)
            await this.getStaff()
            this.showToast('success', 'top-center', `Membre ${actionText} avec succès`)
          } catch (error) {
            this.showToast('danger', 'top-center', `Erreur lors de la modification du statut: ${error.response?.data?.message || error.message}`)
          } finally {
            this.isLoading = false
          }
        }
      } catch (e) {
        // Modal dismissed, do nothing
      }
    },

    showToast(variant, position, text) {
      this.$toast(
        {
          component: ToastificationContent,
          props: {
            title: variant === 'success' ? 'Succès' : 'Erreur',
            icon: variant === 'success' ? 'CheckCircleIcon' : 'AlertCircleIcon',
            text,
            variant,
          },
        },
        { position },
      )
    },

    toggleViewMode() {
      this.viewMode = this.viewMode === 'table' ? 'card' : 'table'
    },

    resetFilters() {
      this.searchTerm = ''
      this.filters = {
        repository: null,
        is_active: null,
      }
      this.getStaff()
    },

    onSearchChange() {
      if (this.searchTimer) clearTimeout(this.searchTimer)
      this.searchTimer = setTimeout(() => {
        this.getStaff()
      }, 500)
    },

    getInitials(name) {
      if (!name) return '?'
      return name
        .split(' ')
        .map(word => word[0])
        .join('')
        .toUpperCase()
    },

    getRoleLabel(role) {
      const map = {
        sales: 'Commercial',
        warehouse_operator: 'Opérateur Dépôt',
        customer_support: 'Support Client',
        accountant: 'Comptable',
      }
      return map[role] || role
    },

    exportToCSV() {
      try {
        // Format data for CSV
        const csvData = this.staffList.map(staff => {
          let depot = ''
          if (staff.repository) {
            if (typeof staff.repository === 'object' && staff.repository) {
              depot = staff.repository.name
            } else {
              depot = staff.repository
            }
          }
          return {
            Nom: staff.username || '',
            Email: staff.email || '',
            Téléphone: staff.phone || '',
            Rôle: this.getRoleLabel(staff.role),
            Dépôt: depot,
            Statut: staff.is_active ? 'Actif' : 'Inactif',
          }
        })

        // Convert to CSV string
        const headers = Object.keys(csvData[0] || {
          Nom: '', Email: '', Téléphone: '', Rôle: '', Dépôt: '', Statut: '',
        }).join(',')
        const rows = csvData.map(obj => Object.values(obj).map(val => `"${val}"`).join(','))
        const csvContent = [headers, ...rows].join('\n')

        // Create blob and trigger download
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
        const link = document.createElement('a')
        const url = URL.createObjectURL(blob)

        link.setAttribute('href', url)
        link.setAttribute('download', `staff_${new Date().toISOString().slice(0, 10)}.csv`)
        link.style.visibility = 'hidden'

        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)

        this.showToast('success', 'top-right', 'Données exportées avec succès')
      } catch (error) {
        this.showToast('danger', 'top-right', "Erreur lors de l'exportation")
      }
    },
  },
}
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-good-table.scss";
@import "@core/scss/vue/libs/vue-select.scss";
@import "@/assets/scss/user-modules.scss";

.supplier-card {
  @extend .user-card;

  transition: all 0.3s ease;
  border: 2px solid transparent;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15) !important;
  }

  &.border-success {
    border-color: rgba(40, 167, 69, 0.3) !important;

    &:hover {
      border-color: rgba(40, 167, 69, 0.6) !important;
    }
  }

  &.border-secondary {
    border-color: rgba(108, 117, 125, 0.3) !important;

    &:hover {
      border-color: rgba(108, 117, 125, 0.6) !important;
    }
  }
}

/* Enhanced styling for v-select */
.enhanced-select {
  .vs__dropdown-toggle {
    padding: 4px 8px;
    border: 1px solid #d8d6de !important;
    border-radius: 0.357rem;
    background-color: #f8f8f8;
    transition: all 0.3s ease-in-out;
    min-height: 38px;

    &:hover {
      border-color: #7367f0 !important;
    }
  }

  &.vs--open .vs__dropdown-toggle {
    border-color: #7367f0 !important;
    box-shadow: 0 3px 10px 0 rgba(115, 103, 240, 0.1);
  }

  .vs__selected {
    margin: 0 2px;
    padding: 0 5px;
    color: #5e5873;
  }

  .vs__selected-options {
    padding: 2px;
  }

  .vs__search::placeholder {
    color: #b9b9c3;
  }

  .vs__actions {
    padding-right: 8px;
  }

  .vs__dropdown-menu {
    padding: 0;
    border: 1px solid #d8d6de;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  }

  .vs__dropdown-option {
    padding: 8px 20px;

    &:hover, &--highlight {
      background: #f8f8f8;
      color: #7367f0;
    }
  }
}

/* Specific override for border visibility */
.border-select .vs__dropdown-toggle {
  border: 1px solid #d8d6de !important;
}

/* Enhanced Table Styling */
.vgt-table {
  border-radius: 0.5rem;
  overflow: hidden;
  box-shadow: 0 4px 25px 0 rgba(0, 0, 0, 0.1);

  .vgt-table-header {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);

    th {
      color: white !important;
      font-weight: 600 !important;
      text-transform: uppercase;
      font-size: 0.75rem;
      letter-spacing: 0.5px;
      border: none !important;
      padding: 1rem 0.75rem;

      &:first-child {
        border-top-left-radius: 0.5rem;
      }

      &:last-child {
        border-top-right-radius: 0.5rem;
      }
    }
  }

  tbody {
    tr {
      transition: all 0.3s ease;

      &:hover {
        background-color: #f8f9fa !important;
        transform: translateY(-1px);
        box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
      }

      td {
        padding: 1rem 0.75rem;
        vertical-align: middle;
        border-bottom: 1px solid #ede7f6;
      }
    }
  }
}

/* Enhanced Action Buttons */
.action-buttons {
  .btn-icon {
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s ease;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
    }
  }
}

/* Avatar enhancements */
.avatar-wrapper {
  position: relative;
  transition: all 0.3s ease;

  &:hover {
    transform: scale(1.05);
  }

  .status-indicator {
    animation: pulse 2s infinite;
  }
}

@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(40, 167, 69, 0.7);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(40, 167, 69, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(40, 167, 69, 0);
  }
}

/* Contact info styling */
.contact-info {
  font-size: 0.875rem;

  .feather-icon {
    opacity: 0.7;
  }
}

/* Enhanced badges */
.badge {
  &.badge-pill {
    padding: 0.5rem 1rem;
    font-size: 0.75rem;
    font-weight: 600;
  }
}

/* Summary cards enhancements */
.card {
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1) !important;
  }
}

.avatar {
  transition: all 0.3s ease;

  &:hover {
    transform: scale(1.1);
  }
}

/* Enhanced search and filter area */
.card .row {
  .form-group {
    label {
      font-weight: 600;
      color: #5e5873;
      margin-bottom: 0.5rem;
    }
  }
}

/* Responsive enhancements */
@media (max-width: 768px) {
  .vgt-table {
    font-size: 0.8rem;

    .action-buttons {
      .btn-icon {
        width: 28px;
        height: 28px;
      }
    }
  }

  .avatar-wrapper {
    width: 36px !important;
    height: 36px !important;
    min-width: 36px !important;
  }
}

/* Loading state improvements */
.spinner-border {
  width: 3rem;
  height: 3rem;
}

/* Enhanced empty state */
.empty-state-icon {
  .feather-icon {
    animation: float 3s ease-in-out infinite;
  }
}

@keyframes float {
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-10px);
  }
}

.status-badge {
  padding: 0.25rem 0.75rem !important;
  font-size: 0.75rem !important;
  min-width: 60px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
</style>
